// EventDraftAdmin — owner-facing event management surface.
//
// Rendered inside EventLandingPage whenever the caller is the event owner
// (Chunk 7 shipped it for drafts; extended to published events so the
// organizer can still manage roster/groups/games/league after publish —
// a player drops, weather postpones, an additional side game gets added,
// etc.). Every write goes through /.netlify/functions/event-admin.
//
// Tab layout (F-081-T1):
//   • League events → League, Roster, Groups, Games, Advanced setup.
//     The League tab is FIRST and absorbs the former "Details" content
//     (publish flow / read-only summary) beneath the season config, so
//     there is no separate Details tab for leagues.
//   • Outing / event → Details, Roster, Groups, Games, Advanced setup.
//     Non-league events have no League tab, so Details stays put as the
//     first tab — folding it away would strand the publish + delete flow.
//
// The Details content (shared by both layouts) branches on
// event.published_at:
//   • Draft     → <PayAndCreate /> (the WS-A publish flow) + delete card
//   • Published → read-only summary + a note that basic-info editing
//                 (name / date / reschedule) is a follow-up chunk. The
//                 Roster / Groups / Games / League tabs already work
//                 post-publish since they don't check published_at.
//
// Data model note — event_groups.day is a text discriminator that predates
// a normalized rounds table. We use "1", "2", "3", … for outing rounds
// (mirrors the way the existing 3-day-outing legacy code uses friday /
// saturday / sunday). For league events, the round selector uses week
// numbers.

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { db } from '../lib/db'
import { useConfirm } from './ConfirmModal'
import PayAndCreate from './PayAndCreate'
import CourseSearch from './CourseSearch'
import { SINGLE_EVENT_ROUND_NUMBER, firstEventRoundFrom, eventRoundFrom, buildPrefillFromEventRound, mergeEventRounds, stashPrefill, sortEventRounds, nextEventRoundNumber, eventRoundToForm, reorderedEventRoundIds } from '../lib/scheduledRound'
import { toDateInputValue, toTimeInputValue, normalizeLeagueConfigForForm } from '../lib/leagueConfigForm'
import SkinsCard from './SkinsCard'
import WolfCard from './WolfCard'
import VegasCard from './VegasCard'
import { useProGate } from './ProGate'

// The middle tabs are identical for every event type; the first tab differs
// (League for leagues, Details otherwise) and 'Advanced setup' is always last.
const TABS_MID = ['Roster', 'Groups', 'Games']

// Web portal LOGIN page (the organizer signs in there to see the full,
// desktop-first schedule view). We deliberately point at the login route —
// tournyflex.com is the marketing/web origin (see ViewOnDesktopCard) and
// /login is its auth entry (TournyFlex-Web/src/App.jsx). Opened in a NEW
// tab so the mobile PWA is never navigated away from or closed.
const WEB_PORTAL_LOGIN_URL = 'https://tournyflex.com/login'

export default function EventDraftAdmin({ event, onChanged }) {
  const isLeague = event?.type === 'league'
  const isPublished = !!event?.published_at
  // F-081-T1 — League events lead with the League tab (which now also holds
  // the former Details content); every other type leads with Details.
  const tabs = useMemo(
    () => (isLeague
      ? ['League', ...TABS_MID, 'Advanced setup']
      : ['Details', ...TABS_MID, 'Advanced setup']),
    [isLeague]
  )
  const [tab, setTab] = useState(tabs[0])
  const [adminView, setAdminView] = useState(null)
  const [loading, setLoading] = useState(true)

  // Keep the active tab valid if the event's type (and therefore the tab set)
  // ever changes underneath us — otherwise the body would render blank.
  useEffect(() => {
    if (!tabs.includes(tab)) setTab(tabs[0])
  }, [tabs, tab])

  const refresh = useCallback(async () => {
    if (!event?.id) return
    setLoading(true)
    try {
      const { data } = await db('get_event_admin_view', { event_id: event.id })
      setAdminView(data || null)
    } catch {
      setAdminView(null)
    }
    setLoading(false)
  }, [event?.id])

  useEffect(() => { refresh() }, [refresh])

  // F-052 / D-268 — count roster players with a REAL name (not blank, not an
  // anonymous "Player N" placeholder) so <PayAndCreate> can block publishing
  // until every scheduled slot is named. Mirrors isPlaceholderName in
  // netlify/functions/_eventGating.js (the server is the enforcer; this is the
  // matching client-side gate + messaging).
  const namedRosterCount = useMemo(() => {
    const roster = adminView?.roster || []
    return roster.filter((r) => {
      const nm = (r?.name || '').trim()
      return nm !== '' && !/^player\s+\d+$/i.test(nm)
    }).length
  }, [adminView])

  return (
    <>
      <div style={{ display: 'flex', gap: 4, overflowX: 'auto', marginBottom: 14 }}>
        {tabs.map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: '8px 14px', border: 'none', borderRadius: 'var(--radius)',
              background: tab === t ? 'var(--gold)' : 'var(--green-dark)',
              color: tab === t ? 'var(--green-deep)' : 'var(--gray-300)',
              fontWeight: tab === t ? 600 : 400,
              cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Details' && !isLeague && (
        <EventDetailsSection
          event={event}
          isPublished={isPublished}
          namedRosterCount={namedRosterCount}
          onChanged={onChanged}
        />
      )}

      {/* B2T2 — the per-round schedule editor: CRUD event_rounds (play_date on
          ANY weekday, tee time, course, which-nine, scramble) with add / edit /
          reorder / remove. Supersedes the B2T1 interim course cards
          (SingleEventCourseCard / OutingCourseCard, kept defined below for their
          reused selectors + B2T1 source-inspection tests but no longer rendered)
          for single events and multi-round outings. Gated to match the Details
          section itself (!isLeague) — that also covers legacy events whose `type`
          is NULL (e.g. 'The Masters of Mulligans' draft and the pre-migration
          outings), which the old 'event'|'outing' allowlist silently excluded so
          their lost schedule could never be re-entered here. Leagues keep the
          League tab's league_rounds editor until the B2T3 cutover. */}
      {tab === 'Details' && !isLeague && (
        <RoundScheduleEditor event={event} adminView={adminView} loading={loading} refresh={refresh} />
      )}

      {tab === 'Roster' && (
        <RosterTab event={event} adminView={adminView} loading={loading} refresh={refresh} />
      )}

      {tab === 'Groups' && (
        <GroupsTab event={event} adminView={adminView} loading={loading} refresh={refresh} />
      )}

      {tab === 'Games' && (
        <GamesTab event={event} adminView={adminView} loading={loading} refresh={refresh} />
      )}

      {tab === 'League' && isLeague && (
        <>
          <LeagueTab event={event} adminView={adminView} loading={loading} refresh={refresh} />
          {/* F-081-T1 — the former Details tab, folded in beneath the season
              config: the publish flow + delete card for drafts, or the
              read-only summary once the league is live. */}
          <EventDetailsSection
            event={event}
            isPublished={isPublished}
            namedRosterCount={namedRosterCount}
            onChanged={onChanged}
          />
        </>
      )}

      {tab === 'Advanced setup' && (
        <AdvancedSetupTab event={event} />
      )}
    </>
  )
}

// ─── Details content (shared) ───────────────────────────────────────────
//
// F-081-T1 — extracted from the old standalone "Details" tab so it can live
// in two places: as the first tab for non-league events, and folded into the
// League tab for leagues. Draft events get the publish flow + delete card;
// published events get the read-only summary.
function EventDetailsSection({ event, isPublished, namedRosterCount, onChanged }) {
  if (isPublished) return <PublishedDetailsTab event={event} />
  return (
    <>
      <EditDraftDetailsCard event={event} onChanged={onChanged} />
      <PayAndCreate
        event_id={event.id}
        players_scheduled={event.players_scheduled}
        rounds_scheduled={event.rounds_scheduled}
        name={event.name}
        named_count={namedRosterCount}
        onPublished={onChanged}
      />
      <DeleteEventCard event={event} />
    </>
  )
}

// ─── Edit draft basics (F-084-T1) ───────────────────────────────────────
//
// A DRAFT event's basics (name, year, players, rounds, group formation) are
// set once at creation and — before this — could never be changed; a wrong
// count/date forced a delete-and-recreate. This card sits above PayAndCreate
// on the draft Details tab. It opens an inline edit form (fields + validation
// mirror EventCreatePage / create-draft-event.js), calls update_event, then
// onChanged() so EventLandingPage re-fetches and the new counts repropagate
// to every tab (Groups round selector, Course & holes round list, roster
// progress, publish fee). Draft-only: the card is only rendered in the draft
// branch, and the server re-checks published_at IS NULL regardless.
//
// The type + URL slug are intentionally NOT editable here: type gates
// creation permissions and the slug is part of the public URL contract; both
// are create-time decisions. Editing them is out of scope for this task.
const EDIT_GROUP_MODE_OPTIONS = [
  { value: 'random',             label: 'Random each round' },
  { value: 'rotating',           label: 'Rotating — everyone plays everyone' },
  { value: 'same_across_rounds', label: 'Same groups every round' },
  { value: 'manual',             label: 'Manual — I\'ll set groups myself' },
]

function EditDraftDetailsCard({ event, onChanged }) {
  const kind = eventNoun(event)
  const now = new Date()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [toast, showToast] = useToast()
  const [form, setForm] = useState(() => ({
    name: event?.name || '',
    year: event?.year || now.getUTCFullYear(),
    players_scheduled: event?.players_scheduled ?? '',
    rounds_scheduled: event?.rounds_scheduled ?? '',
    group_formation_mode: event?.group_formation_mode || 'random',
  }))

  // Re-seed the form whenever the underlying event changes (e.g. after a
  // successful save + refresh, or if another tab edits the row) so a
  // re-open never shows stale values.
  useEffect(() => {
    setForm({
      name: event?.name || '',
      year: event?.year || now.getUTCFullYear(),
      players_scheduled: event?.players_scheduled ?? '',
      rounds_scheduled: event?.rounds_scheduled ?? '',
      group_formation_mode: event?.group_formation_mode || 'random',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.id, event?.name, event?.year, event?.players_scheduled,
      event?.rounds_scheduled, event?.group_formation_mode])

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }))

  const players = Number(form.players_scheduled)
  const rounds = Number(form.rounds_scheduled)
  const yearNum = Number(form.year)
  const canSave =
    form.name.trim().length > 0 && form.name.trim().length <= 200 &&
    Number.isInteger(yearNum) && yearNum >= 2020 && yearNum <= now.getUTCFullYear() + 5 &&
    Number.isInteger(players) && players > 0 && players <= 500 &&
    Number.isInteger(rounds) && rounds > 0 && rounds <= 60 &&
    EDIT_GROUP_MODE_OPTIONS.some(m => m.value === form.group_formation_mode)

  const save = async () => {
    if (!canSave || busy) return
    setBusy(true)
    setError(null)
    try {
      await callAdmin('update_event', {
        event_id: event.id,
        name: form.name.trim(),
        year: yearNum,
        players_scheduled: players,
        rounds_scheduled: rounds,
        group_formation_mode: form.group_formation_mode,
      })
      showToast('Details saved')
      setOpen(false)
      await onChanged?.()
    } catch (e) {
      setError(e.message || 'Could not save changes.')
    } finally {
      setBusy(false)
    }
  }

  if (!open) {
    return (
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div>
            <h3 style={{ margin: 0, marginBottom: 4, fontSize: '1rem' }}>{kind.charAt(0).toUpperCase() + kind.slice(1)} details</h3>
            <p className="text-xs text-muted" style={{ margin: 0 }}>
              {event?.players_scheduled ?? '—'} player{Number(event?.players_scheduled) === 1 ? '' : 's'}
              {' · '}
              {event?.rounds_scheduled ?? '—'} round{Number(event?.rounds_scheduled) === 1 ? '' : 's'}
              {' · '}{event?.year || '—'}
            </p>
          </div>
          <button className="btn btn-ghost" style={{ flexShrink: 0 }} onClick={() => { setError(null); setOpen(true) }}>
            Edit details
          </button>
        </div>
        <Toast msg={toast} />
      </div>
    )
  }

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <h3 style={{ margin: 0, marginBottom: 10, fontSize: '1rem' }}>Edit {kind} details</h3>

      <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Name</p>
      <input
        className="input"
        value={form.name}
        onChange={(e) => set('name', e.target.value)}
        placeholder="Church Outing"
        maxLength={200}
        style={{ marginBottom: 12 }}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 12, marginBottom: 12 }}>
        <div>
          <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Year</p>
          <input
            className="input"
            type="number"
            min={2020}
            max={now.getUTCFullYear() + 5}
            value={form.year}
            onChange={(e) => set('year', e.target.value)}
          />
        </div>
        <div>
          <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Group formation</p>
          <select
            className="input"
            value={form.group_formation_mode}
            onChange={(e) => set('group_formation_mode', e.target.value)}
          >
            {EDIT_GROUP_MODE_OPTIONS.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 12, marginBottom: 6 }}>
        <div>
          <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Players</p>
          <input
            className="input"
            type="number"
            min={1}
            max={500}
            value={form.players_scheduled}
            onChange={(e) => set('players_scheduled', e.target.value)}
            placeholder="12"
          />
        </div>
        <div>
          <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Rounds</p>
          <input
            className="input"
            type="number"
            min={1}
            max={60}
            value={form.rounds_scheduled}
            onChange={(e) => set('rounds_scheduled', e.target.value)}
            placeholder="1"
          />
        </div>
      </div>

      <p className="text-xs text-muted" style={{ marginTop: 0, marginBottom: 12, lineHeight: 1.5 }}>
        Lowering the round count just hides the extra rounds — any groups or
        games you already set up on those rounds are kept, not deleted.
      </p>

      {error && (
        <p className="text-sm" style={{ color: 'var(--danger, #b23)', marginBottom: 10 }}>
          {error}
        </p>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <button className="btn btn-primary" disabled={!canSave || busy} onClick={save}>
          {busy ? 'Saving…' : 'Save changes'}
        </button>
        <button className="btn btn-ghost" disabled={busy} onClick={() => { setError(null); setOpen(false) }}>
          Cancel
        </button>
      </div>
      <Toast msg={toast} />
    </div>
  )
}

// ─── Advanced setup tab (F-046 / D-264) ─────────────────────────────────
//
// A web SIGNPOST, not a per-event deep link. The heavy commissioning
// workflows (tee times, multi-day / multi-course, large rosters,
// season/league config) live on tournyflex.com — but that event-editing
// page doesn't exist yet, so we intentionally link to the site generally
// and never construct a per-event URL that would 404. No QR code (D-264:
// the user is already on their phone; a QR is for cross-device scanning).
// Lowercase noun for the event's actual type. Falls back to 'outing' for the
// legacy default; must include 'event' (the course / organization / admin hosted
// type) so labels never call an Event an "outing".
const eventNoun = (e) => (e?.type === 'league' ? 'league' : e?.type === 'event' ? 'event' : 'outing')

function AdvancedSetupTab({ event }) {
  const kind = eventNoun(event)
  return (
    <div className="card">
      <h3 style={{ margin: 0, marginBottom: 8 }}>Advanced setup</h3>
      <p className="text-sm" style={{ marginTop: 0, marginBottom: 12, lineHeight: 1.5 }}>
        Some setup is easier on a bigger screen — tee times, multi-day or
        multi-course scheduling, large rosters, and full season/league settings.
        These advanced tools are coming to the web at tournyflex.com. Whatever
        you set up on your phone stays the same {kind} — the web will just give
        you more room to work.
      </p>
      <a
        className="btn btn-primary btn-full"
        href="https://tournyflex.com"
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: 'none' }}
      >
        Visit tournyflex.com
      </a>
      <p className="text-xs text-muted" style={{ marginTop: 10, marginBottom: 0 }}>
        It's all one {kind} — the same players and settings, wherever you manage it.
      </p>
    </div>
  )
}

// ─── Delete event (F-045 / D-265) ───────────────────────────────────────
//
// HARD delete (no archive) of a DRAFT event, owner-only. Rendered on the
// draft Details tab only — a published event can carry recorded scores, and
// "one score written once" means a scored event must never be deletable, so
// we don't offer the action once the event is live. The server
// (event-admin.js delete_event) re-checks owner + zero-scores regardless.
// Uses the shared useConfirm()/ConfirmModal pattern (never browser confirm).
function DeleteEventCard({ event }) {
  const navigate = useNavigate()
  const { confirm } = useConfirm()
  const [busy, setBusy] = useState(false)
  const [toast, showToast] = useToast()
  const kind = eventNoun(event)
  const label = 'Delete ' + kind

  const doDelete = async () => {
    if (busy) return
    const okToDelete = await confirm({
      title: label + '?',
      message:
        `This permanently deletes "${event?.name || 'this ' + kind}" and its `
        + `roster, groups, and side games. This can't be undone.`,
      ok: label,
      cancel: 'Keep it',
      danger: true,
    })
    if (!okToDelete) return
    setBusy(true)
    try {
      await callAdmin('delete_event', { event_id: event.id })
      navigate('/events')
    } catch (e) {
      showToast(e.message)
      setBusy(false)
    }
  }

  return (
    <div className="card" style={{ marginTop: 12, borderColor: 'rgba(214,69,69,0.4)' }}>
      <h3 style={{ margin: 0, marginBottom: 6, color: '#ff6b6b' }}>Danger zone</h3>
      <p className="text-xs text-muted" style={{ marginTop: 0, marginBottom: 10, lineHeight: 1.5 }}>
        Deleting a draft removes it for good. Once players start recording
        scores, the {kind} can no longer be deleted.
      </p>
      <button
        className="btn btn-full"
        style={{ background: '#c44', color: 'white', border: 'none' }}
        disabled={busy}
        onClick={doDelete}
      >
        {busy ? 'Deleting…' : label}
      </button>
      <Toast msg={toast} />
    </div>
  )
}

// ─── shared helpers ─────────────────────────────────────────────────────

async function callAdmin(action, payload) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Please sign in again.')
  const res = await fetch('/.netlify/functions/event-admin', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ action, ...payload }),
  })
  const json = await res.json()
  if (!res.ok) {
    const err = new Error(json.error || `${action} failed`)
    err.code = json.code
    throw err
  }
  return json.data
}

function Toast({ msg }) {
  if (!msg) return null
  return (
    <div style={{
      position: 'fixed', bottom: 'calc(84px + env(safe-area-inset-bottom))', left: '50%', transform: 'translateX(-50%)',
      background: 'var(--green-deep)', color: 'white',
      padding: '10px 16px', borderRadius: 8,
      boxShadow: '0 6px 20px rgba(0,0,0,0.2)', zIndex: 900, maxWidth: '90%',
    }}>{msg}</div>
  )
}

function useToast() {
  const [toast, setToast] = useState(null)
  const show = (m) => { setToast(m); setTimeout(() => setToast(null), 3500) }
  return [toast, show]
}

// Lightweight, dismissable info modal (F-081-T6). Used for the roster
// "Click here for instructions" popup. Click the backdrop or "Got it" to
// close; the inner card stops propagation so clicks inside don't dismiss.
function InfoModal({ open, title, onClose, children }) {
  if (!open) return null
  return (
    <div
      onClick={onClose}
      role="presentation"
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '0 20px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{
          background: 'var(--green-dark)',
          border: '1px solid var(--green-mid)',
          borderRadius: 'var(--radius-lg)',
          padding: '24px 20px', maxWidth: 340, width: '100%',
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        }}
      >
        {title && (
          <p style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--cream)', marginBottom: 10, lineHeight: 1.3 }}>
            {title}
          </p>
        )}
        <div style={{ fontSize: '0.875rem', color: 'var(--gray-300)', lineHeight: 1.5 }}>
          {children}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
          <button
            onClick={onClose}
            style={{
              padding: '9px 20px', border: 'none', borderRadius: 'var(--radius)',
              background: 'var(--gold)', color: 'var(--green-deep)',
              fontFamily: 'var(--font-body)', fontSize: '0.875rem',
              fontWeight: 600, cursor: 'pointer',
            }}
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Details tab (published state) ──────────────────────────────────────
//
// A drafted event's Details tab renders <PayAndCreate />; a published
// event's Details tab renders this component instead. Read-only for now —
// editable event basics (name, event_date, reschedule, cancel) land in a
// follow-up chunk. Deliberately points the organizer at the other tabs
// for what CAN be changed today (roster / groups / games / league).
function PublishedDetailsTab({ event }) {
  const TYPE_LABEL = { outing: 'Outing', league: 'League', event: 'Event' }
  const GROUP_MODE_LABEL = {
    random: 'Random each round',
    rotating: 'Rotating — everyone plays everyone',
    same_across_rounds: 'Same groups every round',
    manual: 'Manual — organizer sets groups',
  }
  const Row = ({ label, value }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--green-mid)' }}>
      <span className="text-muted text-xs">{label}</span>
      <span className="text-sm" style={{ fontWeight: 500 }}>{value}</span>
    </div>
  )
  return (
    <div className="card" style={{ padding: '10px 12px' }}>
      <h3 style={{ margin: 0, marginBottom: 8, fontSize: '1rem' }}>Event details</h3>
      <Row label="Name" value={event.name || 'Untitled'} />
      <Row label="Type" value={TYPE_LABEL[event.type] || event.type || '—'} />
      <Row label="Year" value={event.year || '—'} />
      {event.slug && <Row label="URL slug" value={event.slug} />}
      <Row label="Players scheduled" value={event.players_scheduled ?? '—'} />
      <Row label="Rounds scheduled" value={event.rounds_scheduled ?? '—'} />
      {event.group_formation_mode && (
        <Row
          label="Group formation"
          value={GROUP_MODE_LABEL[event.group_formation_mode] || event.group_formation_mode}
        />
      )}
      <div style={{ marginTop: 10, padding: 10, background: 'var(--green-deep)', borderRadius: 6 }}>
        <p className="text-xs text-muted" style={{ margin: 0, marginBottom: 4 }}>To make changes today:</p>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: '0.8rem' }}>
          <li><strong>Roster</strong> — drop or add players (weather no-shows, replacements)</li>
          <li><strong>Groups</strong> — re-shuffle or manually reassign for a round</li>
          <li><strong>Games</strong> — add or end side games</li>
          {event.type === 'league' && (
            <li><strong>League</strong> — regenerate weekly rounds before the season starts</li>
          )}
        </ul>
        <p className="text-muted text-xs" style={{ margin: 0, marginTop: 8 }}>
          Editing name / date / cancelling the event is coming in a follow-up. Contact support if you need to reschedule or refund the organizer fee in the meantime.
        </p>
      </div>
    </div>
  )
}

// ─── Roster tab ─────────────────────────────────────────────────────────

function RosterTab({ event, adminView, loading, refresh }) {
  const roster = adminView?.roster || []
  const [addName, setAddName]   = useState('')
  const [addPhone, setAddPhone] = useState('')
  const [busy, setBusy] = useState(false)
  const [copySource, setCopySource] = useState('')
  const [copyableEvents, setCopyableEvents] = useState([])
  const [csvOpen, setCsvOpen] = useState(false)
  const [csvText, setCsvText] = useState('')
  const [csvSummary, setCsvSummary] = useState(null)
  const [instructionsOpen, setInstructionsOpen] = useState(false)
  const [toast, showToast] = useToast()

  useEffect(() => {
    if (!event?.owner_id) return
    db('list_owned_events_with_roster', {
      owner_id: event.owner_id,
      exclude_event_id: event.id,
    }).then(({ data }) => setCopyableEvents(data || []))
      .catch(() => {})
  }, [event?.owner_id, event?.id])

  const add = async () => {
    if (!addName.trim() || busy) return
    setBusy(true)
    try {
      await callAdmin('add_roster_player', {
        event_id: event.id,
        name: addName.trim(),
        phone: addPhone.trim() || undefined,
      })
      setAddName('')
      setAddPhone('')
      showToast(`Added ${addName.trim()}`)
      await refresh()
    } catch (e) { showToast(e.message) }
    setBusy(false)
  }

  const remove = async (player_id, name) => {
    if (busy) return
    if (!confirm(`Remove ${name} from the roster?`)) return
    setBusy(true)
    try {
      await callAdmin('remove_roster_player', { event_id: event.id, player_id })
      showToast(`Removed ${name}`)
      await refresh()
    } catch (e) { showToast(e.message) }
    setBusy(false)
  }

  const copyFrom = async () => {
    if (!copySource || busy) return
    setBusy(true)
    try {
      const res = await callAdmin('copy_roster_from_event', {
        event_id: event.id, source_event_id: copySource,
      })
      showToast(`Copied ${res?.copied ?? 0} player${res?.copied === 1 ? '' : 's'}`)
      await refresh()
    } catch (e) { showToast(e.message) }
    setBusy(false)
  }

  const parseCsv = () => {
    const lines = csvText.split(/\r?\n/).map(l => l.trim()).filter(Boolean)
    if (lines.length === 0) return []
    let headerCols = null
    const rows = []
    for (const [i, line] of lines.entries()) {
      const cells = line.split(',').map(c => c.trim())
      if (i === 0 && /name/i.test(cells[0]) && cells.length <= 6) {
        headerCols = cells.map(c => c.toLowerCase())
        continue
      }
      if (!headerCols) headerCols = ['name', 'phone', 'email', 'handicap', 'notes']
      const row = {}
      headerCols.forEach((h, idx) => { if (cells[idx]) row[h] = cells[idx] })
      if (row.name) rows.push(row)
    }
    return rows
  }

  const importCsv = async () => {
    const rows = parseCsv()
    if (rows.length === 0) { showToast('No rows detected'); return }
    setBusy(true)
    try {
      const res = await callAdmin('import_roster_csv', { event_id: event.id, rows })
      setCsvSummary(res)
      showToast(`Imported ${res.added}, skipped ${res.skipped}`)
      await refresh()
    } catch (e) { showToast(e.message) }
    setBusy(false)
  }

  if (loading) return <p className="text-muted text-sm">Loading roster…</p>

  const progress = event.players_scheduled
    ? `${roster.length} / ${event.players_scheduled}`
    : `${roster.length}`

  return (
    <>
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h3 style={{ margin: 0 }}>Roster</h3>
          <span className="text-sm text-muted">{progress}</span>
        </div>
        <p className="text-xs" style={{ marginBottom: 12 }}>
          <button
            type="button"
            onClick={() => setInstructionsOpen(true)}
            style={{
              background: 'none', border: 'none', padding: 0,
              color: 'var(--gold)', textDecoration: 'underline',
              cursor: 'pointer', font: 'inherit',
            }}
          >
            Click here for instructions
          </button>
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,2fr) minmax(0,1fr)', gap: 8, marginBottom: 6 }}>
          <input
            className="input" placeholder="Name" value={addName}
            onChange={(e) => setAddName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && add()}
          />
          <input
            className="input" placeholder="Phone (optional)" value={addPhone}
            onChange={(e) => setAddPhone(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && add()}
          />
        </div>
        <button className="btn btn-primary btn-full" disabled={!addName.trim() || busy} onClick={add}>
          Add player
        </button>

        <hr className="divider" />

        <div style={{ display: 'grid', gap: 6 }}>
          {roster.length === 0 && (
            <p className="text-muted text-sm">No players yet — add one above, or paste a list below.</p>
          )}
          {roster.length > 0 && (
            <p className="text-muted text-xs" style={{ margin: '0 0 2px' }}>
              <strong style={{ color: 'var(--gold)' }}>Account</strong> = has a TournyFlex login.
              {' '}<strong>Stub</strong> = name/phone placeholder we hold on their behalf until they sign up.
            </p>
          )}
          {roster.map(r => (
            <div
              key={r.player_id}
              className="card card-sm"
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                <span
                  className={`badge ${r.claimed ? 'badge-gold' : 'badge-gray'}`}
                  style={{ fontSize: '0.65rem', letterSpacing: '0.02em', flexShrink: 0 }}
                  title={r.claimed
                    ? 'This player has a TournyFlex account'
                    : 'Placeholder identity — claims into an account when they sign up with this phone number'}
                >
                  {r.claimed ? 'Account' : 'Stub'}
                </span>
                <div style={{ minWidth: 0 }}>
                  <p style={{ margin: 0, fontWeight: 500 }}>{r.name}</p>
                  {r.phone && <p className="text-muted text-xs" style={{ margin: 0 }}>{r.phone}</p>}
                </div>
              </div>
              <button
                className="btn btn-ghost"
                style={{ fontSize: '0.75rem', padding: '4px 10px', flexShrink: 0 }}
                disabled={busy}
                onClick={() => remove(r.player_id, r.name)}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <h3 style={{ margin: 0, marginBottom: 8 }}>Add several at once</h3>
        <button
          className="btn btn-ghost btn-full"
          onClick={() => setCsvOpen(o => !o)}
        >
          {csvOpen ? 'Hide the list box' : 'Paste a list of players'}
        </button>
        {csvOpen && (
          <div style={{ marginTop: 10 }}>
            <p className="text-xs text-muted" style={{ marginBottom: 6 }}>
              Columns (case-insensitive): <code>name</code>, <code>phone</code>, <code>email</code>, <code>handicap</code>, <code>notes</code>.
              Header row optional. One player per line.
            </p>
            <textarea
              className="input"
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              rows={6}
              placeholder={"name,phone\nMarcus Johnson,555-123-4567\nKevin O'Malley\nElena Reyes,555-987-6543"}
              style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem' }}
            />
            <button
              className="btn btn-primary btn-full"
              style={{ marginTop: 8 }}
              disabled={!csvText.trim() || busy}
              onClick={importCsv}
            >
              Import
            </button>
            {csvSummary && (
              <p className="text-xs text-muted" style={{ marginTop: 6 }}>
                Added {csvSummary.added} · skipped {csvSummary.skipped}
                {csvSummary.errors?.length > 0 && ` · errors ${csvSummary.errors.length}`}
              </p>
            )}
          </div>
        )}
      </div>

      {copyableEvents.length > 0 && (
        <div className="card" style={{ marginBottom: 12 }}>
          <h3 style={{ margin: 0, marginBottom: 8 }}>Copy from prior event</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,2fr) minmax(0,1fr)', gap: 8 }}>
            <select
              className="input"
              value={copySource}
              onChange={(e) => setCopySource(e.target.value)}
            >
              <option value="">Choose an event…</option>
              {copyableEvents.map(ev => (
                <option key={ev.id} value={ev.id}>
                  {ev.year} — {ev.name} ({ev.roster_size} players)
                </option>
              ))}
            </select>
            <button className="btn btn-ghost" disabled={!copySource || busy} onClick={copyFrom}>
              Copy
            </button>
          </div>
        </div>
      )}

      <InfoModal
        open={instructionsOpen}
        title="Adding players"
        onClose={() => setInstructionsOpen(false)}
      >
        Enter name and phone number to link current player accounts or allow players to setup their account. Providing name only will require another player to enter their scores. By sending text notification you agree to have the permission of the player to contact them.
      </InfoModal>

      <Toast msg={toast} />
    </>
  )
}

// ─── Groups tab ─────────────────────────────────────────────────────────

// F-081-T3 — group SIZE is editable after the event/league exists. The server
// (event-admin.js actRandomizeGroups) clamps max_size to 2..8, so the picker
// offers exactly that range; 4 stays the default (the historic hardcoded size,
// a standard foursome). Changing it re-drives the existing randomize handlers —
// no schema change and no new server action.
const GROUP_SIZE_OPTIONS = [2, 3, 4, 5, 6, 7, 8]
const DEFAULT_GROUP_SIZE = 4

// Client-side replica of the server's randomize distribution
// (event-admin.js actRandomizeGroups): Fisher-Yates over the WHOLE roster,
// then chunk into groups of up to `cap` (4), numbered 1..n. Returns objects
// in the SAME shape get_event_admin_view builds for saved groups
// ({id, day, group_number, tee_time, players:[{player_id,name}]}) so the
// render path is identical for a staged preview and a saved round. The ids
// are synthetic ('staged-N') — this computes a preview only, it NEVER writes.
function buildStagedGroups(roster, day, cap = 4) {
  const ids = roster.map(r => r.player_id)
  for (let i = ids.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[ids[i], ids[j]] = [ids[j], ids[i]]
  }
  const nameById = new Map(roster.map(r => [r.player_id, r.name]))
  const out = []
  for (let i = 0; i < ids.length; i += cap) {
    const slice = ids.slice(i, i + cap)
    out.push({
      id: `staged-${out.length + 1}`,
      day: String(day),
      group_number: out.length + 1,
      tee_time: null,
      players: slice.map(pid => ({ player_id: pid, name: nameById.get(pid) })),
    })
  }
  return out
}

function GroupsTab({ event, adminView, loading, refresh }) {
  const roster = adminView?.roster || []
  const groups = adminView?.groups || []
  const isLeague = event?.type === 'league'
  const rounds = adminView?.league_rounds || []
  const { confirm } = useConfirm()

  // For outings/events: round selector is 1..rounds_scheduled with "day"
  // stored as the string form. For leagues: same idea, but the label uses
  // the week's scheduled date if we've generated league_rounds.
  const roundOptions = useMemo(() => {
    if (isLeague && rounds.length > 0) {
      return rounds.map(r => ({
        value: String(r.week_number),
        label: `Week ${r.week_number} · ${r.scheduled_date}`,
      }))
    }
    const n = Number(event?.rounds_scheduled) || 1
    return Array.from({ length: n }, (_, i) => ({
      value: String(i + 1),
      label: `Round ${i + 1}`,
    }))
  }, [event?.rounds_scheduled, isLeague, rounds])

  const [round, setRound] = useState(roundOptions[0]?.value || '1')
  // Staged randomize preview (F-081-T7). Cache-only: Randomize computes a
  // shuffle here WITHOUT any DB write; the saved groups stay untouched until
  // the organizer taps Save. Shape: { round, groups:[<staged group>] } | null.
  const [staged, setStaged] = useState(null)
  useEffect(() => {
    if (!roundOptions.find(r => r.value === round)) {
      setRound(roundOptions[0]?.value || '1')
      // The valid round set changed underneath us — drop any preview so a
      // hidden staged shuffle can't later reappear against a different round.
      setStaged(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundOptions])

  const roundGroups = useMemo(
    () => groups.filter(g => String(g.day) === String(round)),
    [groups, round]
  )
  const [busy, setBusy] = useState(false)
  const [toast, showToast] = useToast()

  // F-081-T3 — group SIZE, editable after creation. Drives both the single-
  // round preview (buildStagedGroups cap) and the all-rounds randomize
  // (server max_size). Transient organizer choice, not persisted on the event;
  // the server re-clamps to 2..8 regardless.
  const [groupSize, setGroupSize] = useState(DEFAULT_GROUP_SIZE)

  // A preview only counts for the round it was shuffled on. When one exists
  // for the active round we render IT (a preview), otherwise the saved groups.
  const stagedForRound =
    staged && String(staged.round) === String(round) ? staged.groups : null
  const displayGroups = stagedForRound || roundGroups

  const assignedPlayerIds = new Set(
    displayGroups.flatMap(g => g.players.map(p => p.player_id))
  )
  const unassigned = roster.filter(r => !assignedPlayerIds.has(r.player_id))

  // Randomize is now CACHE-ONLY: it stages a shuffled preview and writes
  // NOTHING to the DB (an accidental tap can no longer destroy saved groups).
  // Re-tapping while a preview is showing re-shuffles the preview.
  const randomize = () => {
    if (roster.length === 0) { showToast('Add roster players first'); return }
    setStaged({ round, groups: buildStagedGroups(roster, round, groupSize) })
  }

  // Save persists the staged preview via the gated set_round_groups action,
  // which atomically replaces THIS round's groups (delete+recreate scoped to
  // event_id + day). This is the only DB write in the randomize flow.
  const saveStaged = async () => {
    if (!staged || busy) return
    setBusy(true)
    try {
      const payloadGroups = staged.groups.map(g => g.players.map(p => p.player_id))
      const res = await callAdmin('set_round_groups', {
        event_id: event.id, day: staged.round, groups: payloadGroups,
      })
      showToast(`Saved ${res.groups} group${res.groups === 1 ? '' : 's'}`)
      setStaged(null)
      await refresh()
    } catch (e) { showToast(e.message) }
    setBusy(false)
  }

  // Discard drops the preview; the saved groups (untouched in the DB) show
  // again on the next render.
  const discardStaged = () => setStaged(null)

  // Round-switch guard (spec item 4): switching rounds with an unsaved
  // preview would silently hide it, so require an explicit discard first via
  // the shared ConfirmModal (never native confirm()).
  const changeRound = async (next) => {
    if (next === round) return
    if (staged && String(staged.round) === String(round)) {
      const label = roundOptions.find(o => o.value === String(round))?.label || 'this round'
      const okSwitch = await confirm({
        title: 'Discard unsaved shuffle?',
        message:
          `You shuffled ${label} but haven't saved it. Switching rounds will `
          + `discard the preview. Your saved groups are unchanged.`,
        ok: 'Discard & switch',
        cancel: 'Stay here',
        danger: true,
      })
      if (!okSwitch) return
      setStaged(null)
    }
    setRound(next)
  }

  // F-081-T4 — reshuffle groups for EVERY round in one action. NOTE: unlike the
  // single-round Randomize (cache-only preview + Save, F-081-T7), this all-rounds
  // action still writes IMMEDIATELY per round — staging it across N rounds is a
  // follow-up (D-309 extension). Confirmed through the shared ConfirmModal (never
  // native confirm(), which the T7 safety net removed from this tab). Loops the
  // per-round randomize_groups call; if ANY round fails we surface that round's
  // REAL error (with its label) and stop. A single refresh at the end syncs the UI.
  const randomizeAllRounds = async () => {
    if (roster.length === 0) { showToast('Add roster players first'); return }
    const roundCount = roundOptions.length
    const kind = isLeague ? 'league' : 'event'
    const okAll = await confirm({
      title: 'Replace groups on every round?',
      message:
        `This immediately replaces the groups on all ${roundCount} round`
        + `${roundCount === 1 ? '' : 's'} of this ${kind} — it is not a preview and cannot be undone.`,
      ok: 'Replace all rounds',
      cancel: 'Cancel',
      danger: true,
    })
    if (!okAll) return
    setBusy(true)
    let made = 0
    for (const o of roundOptions) {
      try {
        const res = await callAdmin('randomize_groups', {
          event_id: event.id, day: o.value, max_size: groupSize,
        })
        made += res.groups
      } catch (e) {
        showToast(`"${o.label}" failed: ${e.message}`)
        await refresh()
        setBusy(false)
        return
      }
    }
    showToast(`Made ${made} groups across ${roundCount} round${roundCount === 1 ? '' : 's'}`)
    await refresh()
    setBusy(false)
  }

  const addGroup = async () => {
    setBusy(true)
    try {
      await callAdmin('create_group', { event_id: event.id, day: round })
      await refresh()
    } catch (e) { showToast(e.message) }
    setBusy(false)
  }

  const assign = async (group_id, player_id) => {
    if (!player_id) return
    setBusy(true)
    try {
      await callAdmin('assign_player_to_group', { group_id, player_id })
      await refresh()
    } catch (e) { showToast(e.message) }
    setBusy(false)
  }

  const unassign = async (group_id, player_id) => {
    setBusy(true)
    try {
      await callAdmin('remove_player_from_group', { group_id, player_id })
      await refresh()
    } catch (e) { showToast(e.message) }
    setBusy(false)
  }

  if (loading) return <p className="text-muted text-sm">Loading groups…</p>

  return (
    <>
      <div className="card" style={{ marginBottom: 12 }}>
        <p className="text-xs" style={{ marginBottom: 10 }}>
          <a
            href={WEB_PORTAL_LOGIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--gold)', textDecoration: 'underline', cursor: 'pointer' }}
          >
            To view full schedule visit online portal
          </a>
        </p>

        <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Round</p>
        <select
          className="input"
          value={round}
          onChange={(e) => changeRound(e.target.value)}
          disabled={busy}
          style={{ marginBottom: 10 }}
        >
          {roundOptions.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        {/* F-081-T3 — group SIZE, editable after creation. Applies to the next
            Randomize (single-round preview) and Randomize all rounds. */}
        <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Players per group</p>
        <select
          className="input"
          value={groupSize}
          onChange={(e) => setGroupSize(Number(e.target.value))}
          disabled={busy}
          style={{ marginBottom: 10 }}
        >
          {GROUP_SIZE_OPTIONS.map(n => (
            <option key={n} value={n}>{n} per group</option>
          ))}
        </select>

        {stagedForRound ? (
          <>
            <div
              role="status"
              style={{
                background: 'rgba(214,168,69,0.12)',
                border: '1px solid rgba(214,168,69,0.5)',
                borderRadius: 6, padding: '8px 10px', marginBottom: 10,
              }}
            >
              <p className="text-sm" style={{ margin: 0, fontWeight: 600, color: 'var(--gold)' }}>
                Unsaved shuffle — preview only
              </p>
              <p className="text-xs text-muted" style={{ margin: '2px 0 0', lineHeight: 1.5 }}>
                These groups are a preview and have NOT been saved. Your existing
                groups are untouched until you tap Save. Discard restores them; Save
                replaces this round with the shuffle below.
              </p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <button className="btn btn-primary" disabled={busy} onClick={saveStaged}>
                {busy ? 'Saving…' : 'Save groups'}
              </button>
              <button className="btn btn-ghost" disabled={busy} onClick={discardStaged}>
                Discard
              </button>
            </div>
            <button
              className="btn btn-ghost btn-full"
              disabled={busy}
              onClick={randomize}
              style={{ marginTop: 8 }}
            >
              Re-randomize
            </button>
          </>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <button className="btn btn-primary" disabled={busy} onClick={randomize}>
                Randomize
              </button>
              <button className="btn btn-ghost" disabled={busy} onClick={addGroup}>
                + Add group
              </button>
            </div>
            {/* All-rounds reshuffle is immediate (not the single-round preview);
                hidden while a preview is showing so the two can't be confused. */}
            <button
              className="btn btn-ghost btn-full"
              style={{ marginTop: 8 }}
              disabled={busy}
              onClick={randomizeAllRounds}
            >
              Randomize all rounds
            </button>
          </>
        )}
      </div>

      {displayGroups.length === 0 && (
        <div className="card">
          <p className="text-sm text-muted" style={{ margin: 0 }}>
            No groups yet on this round. Tap Randomize (shuffles the whole roster into
            groups of {groupSize}) or Add group to build them manually.
          </p>
        </div>
      )}

      {displayGroups.map(g => (
        <div key={g.id} className="card" style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <h4 style={{ margin: 0 }}>Group {g.group_number}</h4>
            <span className="text-xs text-muted">{g.players.length} / {groupSize}</span>
          </div>
          <div style={{ display: 'grid', gap: 4, marginBottom: 8 }}>
            {g.players.map(p => (
              <div
                key={p.player_id}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '6px 8px', background: 'var(--green-deep)', borderRadius: 6,
                }}
              >
                <span className="text-sm">{p.name}</span>
                {/* Manual edits are immediate (direct DB writes), so they're
                    hidden while a preview is showing — Save or Discard the
                    shuffle first, otherwise an immediate edit would silently
                    hit the SAVED groups behind the preview. */}
                {!stagedForRound && (
                  <button
                    className="btn btn-ghost"
                    style={{ fontSize: '0.7rem', padding: '2px 8px' }}
                    disabled={busy}
                    onClick={() => unassign(g.id, p.player_id)}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
          {!stagedForRound && g.players.length < groupSize && unassigned.length > 0 && (
            <select
              className="input"
              value=""
              onChange={(e) => assign(g.id, e.target.value)}
              disabled={busy}
              style={{ fontSize: '0.85rem' }}
            >
              <option value="">Add player…</option>
              {unassigned.map(u => (
                <option key={u.player_id} value={u.player_id}>{u.name}</option>
              ))}
            </select>
          )}
        </div>
      ))}

      {unassigned.length > 0 && (
        <p className="text-xs text-muted" style={{ marginTop: 6 }}>
          {unassigned.length} roster player{unassigned.length === 1 ? '' : 's'} unassigned on this round.
        </p>
      )}

      <Toast msg={toast} />
    </>
  )
}

// ─── Games tab ──────────────────────────────────────────────────────────

function GamesTab({ event, adminView, loading, refresh }) {
  const roster = adminView?.roster || []
  const isLeague = event?.type === 'league'
  const leagueRounds = adminView?.league_rounds || []

  const [games, setGames] = useState([])
  const [busy, setBusy] = useState(false)
  const [toast, showToast] = useToast()
  // Pro-gate upsell modal. Event/league side games are Pro (event-admin.js
  // 402s a non-Pro create); a 402 pro_required opens this modal instead of
  // a bare toast. Standalone/casual side games stay free (no gate).
  const proGate = useProGate()

  // Create-form state
  const [openForm, setOpenForm] = useState(false)
  const [scope, setScope] = useState('event') // 'event' | 'round'
  const [round, setRound] = useState('1')
  const [format, setFormat] = useState('skins')
  const [unitValue, setUnitValue] = useState('1')
  const [selectedPlayers, setSelectedPlayers] = useState(new Set())
  // Team split for Nassau / Match Play (only when 3+ selected). Ignored
  // for non-teamed formats. Server (event-admin.js splitIntoSides) treats
  // a 2-player teamed game without team_id as legacy 1v1.
  const [teamAssignment, setTeamAssignment] = useState(new Map())
  const [vegasFlip, setVegasFlip] = useState(true) // Vegas flip toggle, default ON
  const [expandedGame, setExpandedGame] = useState(null)
  // F-094-T1: stub-birthdate ack for wager games. `claimed=false` on a
  // roster row means the player hasn't signed up yet → typically no
  // birthdate on file, so the organizer confirms 18+ offline via this
  // checkbox. Server also gates on affirmatively-known minor birthdates
  // (that path always rejects regardless of ack).
  const [stubAgeAck, setStubAgeAck] = useState(false)

  // Formats the server has compute paths for today (Chunk 8 + Chunk 9).
  // Wolf and the rest remain server-side stubs — expose them here as they
  // gain compute paths, not before. Nassau + Match Play are 2-SIDE formats
  // (best-ball per team); any count 2+ works.
  const FORMAT_OPTIONS = [
    { value: 'skins',      label: 'Skins',      min: 2, teamed: false, unitLabel: '$/skin' },
    { value: 'nassau',     label: 'Nassau',     min: 2, teamed: true,  unitLabel: '$/segment (front/back/overall)' },
    { value: 'stableford', label: 'Stableford', min: 2, teamed: false, unitLabel: '$/round (winner-take-all)' },
    { value: 'simple',     label: 'Simple',     min: 2, teamed: false, unitLabel: '$/hole per opponent' },
    { value: 'match_1v1',  label: 'Match Play', min: 2, teamed: true,  unitLabel: '$/match' },
    { value: 'wolf',       label: 'Wolf',       min: 3, teamed: false, wolf: true,  unitLabel: '$/point' },
    { value: 'vegas',      label: 'Vegas',      min: 4, teamed: true,  vegas: true, unitLabel: '$/point' },
  ]
  const currentFormat = FORMAT_OPTIONS.find(f => f.value === format) || FORMAT_OPTIONS[0]

  // F-094-T1: wager-enabled = unit > 0 on a wagered format. Event side
  // games don't include a wagerless format today, but be defensive.
  const wagerEnabled = !currentFormat.wagerless && (Number(unitValue) || 0) > 0
  // Any selected roster row that hasn't yet signed up (claimed=false) is
  // treated as birthdate-unknown → ack is required. Over-shows the ack
  // for a commissioner-added stub that DOES carry a birthdate; never
  // wrongly hides it. See F-094-T1 spec risk 3.
  const anyUnclaimedSelected = useMemo(() => {
    if (!wagerEnabled) return false
    for (const pid of selectedPlayers) {
      const r = roster.find(x => x.player_id === pid)
      if (r && !r.claimed) return true
    }
    return false
  }, [wagerEnabled, selectedPlayers, roster])
  const stubAckRequired = wagerEnabled && anyUnclaimedSelected

  const teamCounts = useMemo(() => {
    let a = 0, b = 0
    for (const pid of selectedPlayers) {
      const t = teamAssignment.get(pid)
      if (t === 'A') a += 1
      else if (t === 'B') b += 1
    }
    return { a, b }
  }, [selectedPlayers, teamAssignment])

  const roundOptions = useMemo(() => {
    if (isLeague && leagueRounds.length > 0) {
      return leagueRounds.map(r => ({
        value: String(r.week_number),
        label: `Week ${r.week_number} · ${r.scheduled_date}`,
      }))
    }
    const n = Number(event?.rounds_scheduled) || 1
    return Array.from({ length: n }, (_, i) => ({
      value: String(i + 1),
      label: `Round ${i + 1}`,
    }))
  }, [event?.rounds_scheduled, isLeague, leagueRounds])

  const loadGames = useCallback(async () => {
    if (!event?.id) return
    const { data } = await db('list_games_for_event', { event_id: event.id })
    setGames(data || [])
  }, [event?.id])

  useEffect(() => { loadGames() }, [loadGames])

  const togglePlayer = (pid) => {
    setSelectedPlayers(prev => {
      const next = new Set(prev)
      if (next.has(pid)) next.delete(pid); else next.add(pid)
      return next
    })
  }
  const setTeam = (pid, team) => {
    setTeamAssignment(prev => {
      const next = new Map(prev)
      if (next.get(pid) === team) next.delete(pid)
      else next.set(pid, team)
      return next
    })
  }
  const selectAll = () => setSelectedPlayers(new Set(roster.map(r => r.player_id)))
  const selectNone = () => setSelectedPlayers(new Set())

  const createGame = async () => {
    const n = selectedPlayers.size
    if (n < currentFormat.min) {
      showToast(`${currentFormat.label} needs at least ${currentFormat.min} players`); return
    }
    if (currentFormat.teamed && n > 2 && (teamCounts.a === 0 || teamCounts.b === 0)) {
      showToast('Assign every player to Team A or Team B before creating.'); return
    }
    if (currentFormat.vegas && (n !== 4 || teamCounts.a !== 2 || teamCounts.b !== 2)) {
      // Exactly 4 AND balanced 2v2 — a 5th selected-but-unassigned player
      // would otherwise pass teamCounts (2/2) yet ship a 5-participant
      // payload the server rejects (or worse, buckets into a 3A/2B game).
      showToast('Vegas needs exactly 4 players — 2 on Team A and 2 on Team B.'); return
    }
    // F-094-T1: block create if a stub-heavy wager doesn't carry the ack.
    if (stubAckRequired && !stubAgeAck) {
      showToast('Confirm every participant is 18+ before creating a wager game.'); return
    }
    // Format-specific config shapes match the server compute paths
    // (netlify/functions/db.js get_*_state + event-admin.js settlement).
    const unit = Number(unitValue) || 0
    let config
    switch (format) {
      case 'skins':
        config = { scoring_mode: 'gross', carry_from_ties: true, unit_value: unit }
        break
      case 'nassau':
        // One input, applied equally to all three segments. Advanced per-
        // segment betting is a later polish.
        config = { bet_units: { front: unit, back: unit, overall: unit } }
        break
      case 'stableford':
      case 'simple':
        config = { unit_value: unit }
        break
      case 'match_1v1':
        config = { bet_units: unit }
        break
      case 'wolf':
        // Rotation follows the selected-player order (Set insertion order).
        // Wolf decisions are recorded from the interactive WolfCard in this
        // Games tab (expand a live game → "View"), which routes
        // set_wolf_decision to event-admin for event-scoped games.
        config = {
          unit_value: unit,
          rotation_mode: 'scorecard',
          rotation_order: Array.from(selectedPlayers),
          blind_wolf_enabled: true,
        }
        break
      case 'vegas':
        config = { unit_value: unit, flip_enabled: vegasFlip, scoring_mode: 'gross' }
        break
      default:
        config = { unit_value: unit }
    }
    // F-094-T1: forward the ack flag to the server. Server rewrites
    // `true` → `{ by: caller.id, at: iso }` on the persisted config for
    // the audit trail; ack is only present for wager-enabled games.
    if (stubAckRequired && stubAgeAck) {
      config.stub_age_ack = true
    }
    const payload = Array.from(selectedPlayers).map(pid => ({
      player_id: pid,
      team_id: currentFormat.teamed ? (teamAssignment.get(pid) || null) : null,
    }))
    setBusy(true)
    try {
      await callAdmin('create_game', {
        event_id: event.id,
        round_day: scope === 'round' ? round : undefined,
        format,
        config,
        participants: payload,
      })
      showToast(`${currentFormat.label} game created.`)
      setOpenForm(false)
      setSelectedPlayers(new Set())
      setTeamAssignment(new Map())
      setVegasFlip(true)
      setStubAgeAck(false)
      await loadGames()
    } catch (e) {
      // Side games inside an event/league require Pro — surface the upsell
      // MODAL, not a bare toast. The create form stays open so the organizer
      // can retry after upgrading. Every other error still toasts.
      if (e.code === 'pro_required') {
        proGate.open({ feature: 'side-games' })
      } else {
        showToast(e.message)
      }
    }
    setBusy(false)
  }

  const endGame = async (game_id) => {
    if (!confirm('End this game? It stays visible in history but stops updating.')) return
    setBusy(true)
    try {
      await callAdmin('end_game', { game_id })
      showToast('Game ended.')
      await loadGames()
    } catch (e) { showToast(e.message) }
    setBusy(false)
  }

  const removeParticipant = async (game_id, player_id) => {
    setBusy(true)
    try {
      await callAdmin('remove_game_participant', { game_id, player_id })
      await loadGames()
    } catch (e) { showToast(e.message) }
    setBusy(false)
  }

  const addParticipant = async (game_id, player_id) => {
    if (!player_id) return
    setBusy(true)
    try {
      await callAdmin('add_game_participant', { game_id, player_id })
      await loadGames()
    } catch (e) { showToast(e.message) }
    setBusy(false)
  }

  if (loading) return <p className="text-muted text-sm">Loading games…</p>

  if (roster.length === 0) {
    return (
      <div className="card">
        <p className="text-sm text-muted" style={{ margin: 0 }}>
          Add roster players first — every side game needs at least 2 participants.
        </p>
      </div>
    )
  }

  return (
    <>
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h3 style={{ margin: 0 }}>Side games</h3>
          <button
            className="btn btn-ghost"
            style={{ fontSize: '0.8rem' }}
            onClick={() => setOpenForm(o => !o)}
          >
            {openForm ? 'Cancel' : '+ New game'}
          </button>
        </div>

        {openForm && (
          <div style={{ borderTop: '1px solid var(--green-mid)', paddingTop: 10 }}>
            <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Scope</p>
            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
              <button
                className={`btn ${scope === 'round' ? 'btn-primary' : 'btn-ghost'}`}
                style={{ flex: 1, fontSize: '0.8rem' }}
                onClick={() => setScope('round')}
              >
                Single round
              </button>
              <button
                className={`btn ${scope === 'event' ? 'btn-primary' : 'btn-ghost'}`}
                style={{ flex: 1, fontSize: '0.8rem' }}
                onClick={() => setScope('event')}
              >
                Season-long
              </button>
            </div>

            {scope === 'round' && (
              <>
                <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Round</p>
                <select className="input" value={round} onChange={(e) => setRound(e.target.value)}
                        style={{ marginBottom: 10 }}>
                  {roundOptions.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </>
            )}

            <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Format</p>
            <select
              className="input"
              value={format}
              onChange={(e) => setFormat(e.target.value)}
              style={{ marginBottom: 10 }}
            >
              {FORMAT_OPTIONS.map(f => (
                <option key={f.value} value={f.value}>
                  {f.label}{f.teamed ? ' — 2 sides' : ''}
                </option>
              ))}
            </select>

            <p className="text-xs text-muted" style={{ marginBottom: 4 }}>
              {currentFormat.unitLabel} (0 = bragging rights only)
            </p>
            <input className="input" type="number" min={0} step="0.25"
                   value={unitValue}
                   onChange={(e) => setUnitValue(e.target.value)}
                   style={{ marginBottom: 10 }} />
            <p className="text-muted text-xs" style={{ margin: '-6px 0 10px' }}>
              Money amounts are display-only. TournyFlex never handles wager funds.
            </p>

            {currentFormat.vegas && (
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 10, fontSize: '0.82rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={vegasFlip}
                  onChange={(e) => setVegasFlip(e.target.checked)}
                  style={{ marginTop: 2 }}
                />
                <span>
                  Flip on natural birdie
                  <span style={{ display: 'block', color: 'var(--gray-400)', fontSize: '0.72rem' }}>
                    Opponent's natural birdie flips your number high; a natural eagle doubles the hole. Two teams of two.
                  </span>
                </span>
              </label>
            )}
            {currentFormat.wolf && (
              <p className="text-muted text-xs" style={{ margin: '-4px 0 10px' }}>
                Wolf rotation follows the order you select players below. Players
                record their partner/lone/blind choice from the scorecard.
              </p>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <p className="text-xs text-muted" style={{ margin: 0 }}>
                Participants ({selectedPlayers.size} / {roster.length})
                {currentFormat.teamed && selectedPlayers.size > 2 && (
                  <span style={{ marginLeft: 6 }}>· A: {teamCounts.a} · B: {teamCounts.b}</span>
                )}
              </p>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-ghost" style={{ fontSize: '0.7rem', padding: '2px 8px' }} onClick={selectAll}>All</button>
                <button className="btn btn-ghost" style={{ fontSize: '0.7rem', padding: '2px 8px' }} onClick={selectNone}>None</button>
              </div>
            </div>
            <div style={{ display: 'grid', gap: 4, marginBottom: 12, maxHeight: 240, overflowY: 'auto' }}>
              {roster.map(r => {
                const on = selectedPlayers.has(r.player_id)
                const team = teamAssignment.get(r.player_id)
                return (
                  <div key={r.player_id} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '6px 8px', borderRadius: 6,
                    background: on ? 'var(--green-deep)' : 'transparent',
                  }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, cursor: 'pointer' }}>
                      <input type="checkbox" checked={on} onChange={() => togglePlayer(r.player_id)} />
                      <span className="text-sm">{r.name}</span>
                    </label>
                    {on && currentFormat.teamed && selectedPlayers.size > 2 && (
                      <div style={{ display: 'flex', gap: 4 }}>
                        {['A', 'B'].map(t => (
                          <button
                            key={t}
                            onClick={() => setTeam(r.player_id, t)}
                            style={{
                              padding: '2px 8px', borderRadius: 4,
                              border: `1px solid ${team === t ? 'var(--gold)' : 'var(--green-mid)'}`,
                              background: team === t ? 'var(--gold)' : 'transparent',
                              color: team === t ? 'var(--green-deep)' : 'var(--cream)',
                              fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer',
                            }}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {stubAckRequired && (
              <label
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                  marginBottom: 10, padding: '8px 10px', borderRadius: 6,
                  background: 'rgba(212,185,122,0.08)', border: '1px solid var(--gold)',
                  fontSize: '0.82rem', cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={stubAgeAck}
                  onChange={(e) => setStubAgeAck(e.target.checked)}
                  style={{ marginTop: 3 }}
                />
                <span>
                  I confirm every participant in this wager is 18 or older.
                  <span style={{ display: 'block', color: 'var(--gray-400)', fontSize: '0.72rem', marginTop: 2 }}>
                    Required whenever a wager includes players who haven't signed up yet.
                  </span>
                </span>
              </label>
            )}

            <button
              className="btn btn-primary btn-full"
              disabled={
                busy
                || selectedPlayers.size < currentFormat.min
                || (currentFormat.teamed && selectedPlayers.size > 2 && (teamCounts.a === 0 || teamCounts.b === 0))
                || (currentFormat.vegas && (selectedPlayers.size !== 4 || teamCounts.a !== 2 || teamCounts.b !== 2))
                || (stubAckRequired && !stubAgeAck)
              }
              onClick={createGame}
            >
              Create {currentFormat.label}
            </button>
          </div>
        )}
      </div>

      {games.length === 0 && !openForm && (
        <div className="card">
          <p className="text-sm text-muted" style={{ margin: 0 }}>
            No side games yet. Tap "+ New game" above to create one — Skins, Nassau, Stableford, Simple, or Match Play.
          </p>
        </div>
      )}

      {games.map(g => {
        const activeParticipants = g.participants.filter(p => !p.left_at)
        const inGame = new Set(activeParticipants.map(p => p.player_id))
        const notInGame = roster.filter(r => !inGame.has(r.player_id))
        return (
          <div key={g.id} className="card" style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h4 style={{ margin: 0, textTransform: 'capitalize' }}>{g.format}</h4>
                <p className="text-xs text-muted" style={{ margin: 0 }}>
                  {g.round_day ? `Round ${g.round_day}` : 'Season-long'}
                  {g.ended_at ? ' · ended' : ''}
                </p>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  className="btn btn-ghost"
                  style={{ fontSize: '0.75rem' }}
                  onClick={() => setExpandedGame(expandedGame === g.id ? null : g.id)}
                >
                  {expandedGame === g.id ? 'Hide' : 'View'}
                </button>
                {!g.ended_at && (
                  <button
                    className="btn btn-ghost"
                    style={{ fontSize: '0.75rem' }}
                    onClick={() => endGame(g.id)}
                    disabled={busy}
                  >
                    End
                  </button>
                )}
              </div>
            </div>

            <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {activeParticipants.map(p => (
                <span key={p.player_id} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '2px 8px', borderRadius: 999,
                  background: 'var(--green-deep)', fontSize: '0.75rem',
                }}>
                  {p.name}
                  {!g.ended_at && (
                    <button
                      onClick={() => removeParticipant(g.id, p.player_id)}
                      style={{ background: 'none', border: 'none', color: 'var(--gray-400)', cursor: 'pointer', padding: 0, fontSize: '0.8rem' }}
                      disabled={busy}
                    >×</button>
                  )}
                </span>
              ))}
              {!g.ended_at && notInGame.length > 0 && (
                <select
                  className="input"
                  value=""
                  onChange={(e) => addParticipant(g.id, e.target.value)}
                  disabled={busy}
                  style={{ fontSize: '0.75rem', width: 'auto', padding: '2px 6px' }}
                >
                  <option value="">+ add</option>
                  {notInGame.map(u => (
                    <option key={u.player_id} value={u.player_id}>{u.name}</option>
                  ))}
                </select>
              )}
            </div>

            {expandedGame === g.id && g.format === 'skins' && (
              <div style={{ marginTop: 12 }}>
                <SkinsCard game_id={g.id} />
              </div>
            )}
            {expandedGame === g.id && g.format === 'wolf' && (
              <div style={{ marginTop: 12 }}>
                {!g.ended_at && (
                  <p className="text-xs text-muted" style={{ margin: '0 0 8px' }}>
                    Record each hole's Wolf decision here — partner, Lone, or Blind.
                    Decisions save to the event and feed the settlement recap.
                  </p>
                )}
                {/* Live event Wolf: the interactive picker (non-readOnly)
                    routes set_wolf_decision to event-admin because the
                    game is event-scoped (standalone_round_id is null).
                    Ended games fall back to the read-only recap. */}
                <WolfCard game_id={g.id} readOnly={!!g.ended_at} autoRefreshMs={5000} />
              </div>
            )}
            {expandedGame === g.id && g.format === 'vegas' && (
              <div style={{ marginTop: 12 }}>
                <VegasCard game_id={g.id} autoRefreshMs={5000} />
              </div>
            )}
          </div>
        )
      })}

      <Toast msg={toast} />
      {proGate.element}
    </>
  )
}

// ─── League tab ─────────────────────────────────────────────────────────

// Which-nine options (F-080). 'full' plays the whole course (9 or 18 holes,
// per the course's hole count); 'front'/'back' are the two nines of an 18.
const HOLE_SET_OPTIONS = [
  { value: 'full',  label: 'Full course' },
  { value: 'front', label: 'Front 9 (holes 1–9)' },
  { value: 'back',  label: 'Back 9 (holes 10–18)' },
]

// Short human label for a league_round's hole_set + course hole count.
function holeSetLabel(holeSet, courseHoles) {
  if (holeSet === 'front') return 'Front 9'
  if (holeSet === 'back') return 'Back 9'
  const n = Number(courseHoles)
  return Number.isFinite(n) && n > 0 ? `${n} holes` : 'Full course'
}

// A course name from a CourseSearch selection (holes_data array or a holes
// count) — normalized to { id, name, holes } for the F-080 course pickers.
function normalizeCoursePick(c) {
  if (!c?.id) return null
  return { id: c.id, name: c.name, holes: c.holes ?? c.holes_data?.length ?? null }
}

// ─── Single-event course card (F-080 req 3; event_rounds cutover B2T1) ───
//
// A single one-off event ('event' type) has exactly ONE round. We persist its
// course + which-nine to an event_rounds row at round_number=1 (event_rounds is
// the schedule source of truth — B2T1 — superseding the interim league_rounds
// storage this card used). The value round-trips through
// get_event_admin_view.event_rounds (LEFT JOIN courses). SINGLE_EVENT_ROUND_NUMBER
// + firstEventRoundFrom (the pure round-1 selector) live in ../lib/scheduledRound
// so the selector is unit-testable without pulling this component's supabase
// import chain. The write rides event-admin.js's set_event_round action.
function SingleEventCourseCard({ event, adminView, loading, refresh }) {
  const [toast, showToast] = useToast()
  const [busy, setBusy] = useState(false)
  // Merge event_rounds (the B2T1 write source of truth) with a league_rounds
  // read-time fallback so a course pinned BEFORE the B2 cutover (persisted to
  // league_rounds) still surfaces here instead of showing "No course assigned".
  const mergedRounds = useMemo(
    () => mergeEventRounds(adminView?.event_rounds, adminView?.league_rounds),
    [adminView],
  )
  const saved = useMemo(() => firstEventRoundFrom(mergedRounds), [mergedRounds])
  const [course, setCourse] = useState(null)   // { id, name, holes }
  const [holeSet, setHoleSet] = useState('full')
  // Hydrate the pickers from the saved round-1 row ONCE it first arrives, so a
  // returning organizer sees their pinned course without clobbering a live edit.
  const [synced, setSynced] = useState(false)
  useEffect(() => {
    if (synced || !saved) return
    if (saved.course_id) {
      setCourse({ id: saved.course_id, name: saved.course_name, holes: saved.course_holes })
    }
    setHoleSet(saved.which_nine || 'full')
    setSynced(true)
  }, [saved, synced])

  const save = async () => {
    if (busy) return
    if (!course?.id) { showToast('Pick a course first.'); return }
    setBusy(true)
    try {
      await callAdmin('set_event_round', {
        event_id: event.id,
        round_number: SINGLE_EVENT_ROUND_NUMBER,
        course_id: course.id,
        which_nine: holeSet,
      })
      showToast('Course saved.')
      await refresh()
    } catch (e) { showToast(e.message) }
    setBusy(false)
  }

  if (loading) return <p className="text-muted text-sm">Loading course…</p>

  return (
    <>
      <div className="card" style={{ marginTop: 12 }}>
        <h3 style={{ margin: 0, marginBottom: 8 }}>Course &amp; holes</h3>
        <p className="text-xs text-muted" style={{ marginBottom: 8 }}>
          Pin the course and which nine for this event.
        </p>
        {course ? (
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '8px 10px', background: 'var(--green-deep)', borderRadius: 6, marginBottom: 10,
          }}>
            <span className="text-sm">{course.name}</span>
            <button className="btn btn-ghost" style={{ fontSize: '0.75rem' }}
              onClick={() => setCourse(null)}>Change</button>
          </div>
        ) : (
          <div style={{ marginBottom: 10 }}>
            <CourseSearch onSelect={(c) => setCourse(normalizeCoursePick(c))} />
          </div>
        )}
        <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Which holes</p>
        <select className="input" value={holeSet}
          onChange={(e) => setHoleSet(e.target.value)} style={{ marginBottom: 10 }}>
          {HOLE_SET_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <button className="btn btn-primary btn-full" disabled={busy} onClick={save}>
          {busy ? 'Saving…' : saved?.course_id ? 'Update course' : 'Save course'}
        </button>
        {saved?.course_id && (
          <p className="text-xs text-muted" style={{ marginTop: 8 }}>
            Saved: {saved.course_name} · {holeSetLabel(saved.which_nine, saved.course_holes)}
          </p>
        )}
      </div>
      <Toast msg={toast} />
    </>
  )
}

// ─── Outing course card (F-080-T2; event_rounds cutover B2T1) ────────────
//
// A multi-day / multi-round outing ('outing' type) has N rounds
// (events.rounds_scheduled). Each round pins its OWN course + which-nine,
// stored in event_rounds (the schedule source of truth — B2T1): one row per
// round, round_number = the round index (1..N). Mirrors the LeagueTab editor:
// a 'Same course every round' vs 'Varies per round' toggle. 'Same' upserts one
// course_id + which_nine onto every round; 'Varies' gives a per-round editor.
// Rows are upserted via set_event_round (one call per round). The Rounds list
// also carries a per-round "Start round" that pre-fills PlayPage with that
// round's course + holes + which-nine (buildPrefillFromEventRound → stashPrefill),
// WITHOUT stamping a league_round_id (an outing is not a league; the
// event_round_id score link is B2T3). The legacy per-day course columns have
// been retired (D-341 Phase-3 cutover) — course display here comes from
// event_rounds only.
function OutingCourseCard({ event, adminView, loading, refresh }) {
  const navigate = useNavigate()
  const [toast, showToast] = useToast()
  const [busy, setBusy] = useState(false)
  const roundCount = Math.max(1, Number(event?.rounds_scheduled) || 1)
  // Merge event_rounds (the B2T1 write source of truth) with a league_rounds
  // read-time fallback so per-round courses pinned BEFORE the B2 cutover
  // (persisted to league_rounds) still surface here instead of "No course
  // assigned". Saving always writes event_rounds, so the fallback retires on
  // the next save. This is separate from the classic day-column display
  // fallback below (LEGACY_DAYS), which covers the even-older 3-day outings.
  const rounds = useMemo(
    () => mergeEventRounds(adminView?.event_rounds, adminView?.league_rounds),
    [adminView],
  )

  // Legacy 3-day fallback: round 1→friday, 2→saturday, 3→sunday. DISPLAY ONLY —
  // never written, never cleared. The 5 legacy pages (Scorecard/Leaderboard/
  // Groups/Teams/Commissioner) still read these columns unchanged, so classic
  // outings never lose their course; this just surfaces it in the new editor.
  const LEGACY_DAYS = ['friday', 'saturday', 'sunday']
  const legacyCourseName = (roundIndex) => {
    const day = LEGACY_DAYS[roundIndex - 1]
    return day ? (event?.[`${day}_course_name`] || null) : null
  }

  // Mode: 'same' (one course for all rounds) vs 'varies' (per round). Inferred
  // from existing league_rounds so a returning owner lands on the right mode.
  const [courseMode, setCourseMode] = useState('same')
  const [sameCourse, setSameCourse] = useState(null)   // { id, name, holes }
  const [sameHoleSet, setSameHoleSet] = useState('full')
  const [editRound, setEditRound] = useState(null)
  const [editCourse, setEditCourse] = useState(null)
  const [editHoleSet, setEditHoleSet] = useState('full')

  const modeInferred = useMemo(() => {
    const withCourse = rounds.filter(r => r.course_id)
    if (!withCourse.length) return null
    const first = withCourse[0].course_id
    const allSame = withCourse.every(r => r.course_id === first)
    return allSame ? 'same' : 'varies'
  }, [rounds])

  const [modeSynced, setModeSynced] = useState(false)
  useEffect(() => {
    if (modeSynced || modeInferred == null) return
    setCourseMode(modeInferred)
    if (modeInferred === 'same') {
      const withCourse = rounds.find(r => r.course_id)
      if (withCourse) {
        setSameCourse({ id: withCourse.course_id, name: withCourse.course_name, holes: withCourse.course_holes })
        setSameHoleSet(withCourse.which_nine || 'full')
      }
    }
    setModeSynced(true)
  }, [modeInferred, modeSynced, rounds])

  // 'Same' writes the SAME course + hole_set to every round (lazy upserts,
  // one set_league_round per round). Sequential so the max:1 postgres client
  // behind event-admin isn't hit with concurrent writes.
  const saveSame = async () => {
    if (busy) return
    if (!sameCourse?.id) { showToast('Pick a course first.'); return }
    setBusy(true)
    try {
      for (let i = 1; i <= roundCount; i++) {
        await callAdmin('set_event_round', {
          event_id: event.id, round_number: i,
          course_id: sameCourse.id, which_nine: sameHoleSet,
        })
      }
      showToast(`Course set for all ${roundCount} rounds.`)
      await refresh()
    } catch (e) { showToast(e.message) }
    setBusy(false)
  }

  const saveRound = async () => {
    if (busy || editRound == null) return
    setBusy(true)
    try {
      await callAdmin('set_event_round', {
        event_id: event.id, round_number: editRound,
        course_id: editCourse?.id || null, which_nine: editHoleSet,
      })
      showToast(`Round ${editRound} course saved.`)
      setEditRound(null); setEditCourse(null); setEditHoleSet('full')
      await refresh()
    } catch (e) { showToast(e.message) }
    setBusy(false)
  }

  // Round-start prefill: stash BEFORE navigating so it survives the F-074
  // SW-update reload / WebView remount on the →/play hop (same transport the
  // scheduled-round + league tiles use). Uses the outing mapper (no
  // league_round_id) so the round is not routed through league standings.
  const startRound = (roundIndex) => {
    const saved = eventRoundFrom(rounds, roundIndex)
    if (!saved?.course_id) { showToast('Pick a course for this round first.'); return }
    const prefill = buildPrefillFromEventRound(saved)
    stashPrefill(prefill)
    navigate('/play', { state: { prefill } })
  }

  if (loading) return <p className="text-muted text-sm">Loading course…</p>

  return (
    <>
      <div className="card" style={{ marginTop: 12 }}>
        <h3 style={{ margin: 0, marginBottom: 8 }}>Course &amp; holes</h3>
        <p className="text-xs text-muted" style={{ marginBottom: 6 }}>
          Pick the course and which nine for each of this outing's {roundCount} rounds.
        </p>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <button
            type="button"
            className={`btn ${courseMode === 'same' ? 'btn-primary' : 'btn-ghost'}`}
            style={{ flex: 1, fontSize: '0.8rem' }}
            onClick={() => setCourseMode('same')}
          >
            Same course every round
          </button>
          <button
            type="button"
            className={`btn ${courseMode === 'varies' ? 'btn-primary' : 'btn-ghost'}`}
            style={{ flex: 1, fontSize: '0.8rem' }}
            onClick={() => setCourseMode('varies')}
          >
            Varies per round
          </button>
        </div>

        {courseMode === 'same' ? (
          <>
            {sameCourse ? (
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 10px', background: 'var(--green-deep)', borderRadius: 6, marginBottom: 10,
              }}>
                <span className="text-sm">{sameCourse.name}</span>
                <button className="btn btn-ghost" style={{ fontSize: '0.75rem' }}
                  onClick={() => setSameCourse(null)}>Change</button>
              </div>
            ) : (
              <div style={{ marginBottom: 10 }}>
                <CourseSearch onSelect={(c) => setSameCourse(normalizeCoursePick(c))} />
              </div>
            )}
            <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Which holes</p>
            <select className="input" value={sameHoleSet}
              onChange={(e) => setSameHoleSet(e.target.value)} style={{ marginBottom: 10 }}>
              {HOLE_SET_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <button className="btn btn-primary btn-full" disabled={busy} onClick={saveSame}>
              {busy ? 'Saving…' : `Save course for all ${roundCount} rounds`}
            </button>
          </>
        ) : (
          <p className="text-xs text-muted">
            Set each round's course and which-nine in the list below.
          </p>
        )}
      </div>

      <div className="card" style={{ marginTop: 12 }}>
        <h3 style={{ margin: 0, marginBottom: 8 }}>Rounds</h3>
        <div style={{ display: 'grid', gap: 6 }}>
          {Array.from({ length: roundCount }, (_, i) => i + 1).map(roundIndex => {
            const saved = eventRoundFrom(rounds, roundIndex)
            const legacyName = saved?.course_id ? null : legacyCourseName(roundIndex)
            return (
              <div key={roundIndex} style={{
                padding: '8px 10px', background: 'var(--green-deep)', borderRadius: 6,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span className="text-sm">Round {roundIndex}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                  <span className="text-xs" style={{ color: (saved?.course_name || legacyName) ? 'var(--cream)' : 'var(--gray-400)' }}>
                    {saved?.course_name
                      ? `${saved.course_name} · ${holeSetLabel(saved.which_nine, saved.course_holes)}`
                      : legacyName
                        ? `${legacyName} · from classic setup`
                        : 'No course assigned'}
                  </span>
                  {courseMode === 'varies' && (
                    <button
                      className="btn btn-ghost"
                      style={{ fontSize: '0.72rem' }}
                      disabled={busy}
                      onClick={() => {
                        if (editRound === roundIndex) { setEditRound(null); return }
                        setEditRound(roundIndex)
                        setEditCourse(saved?.course_id ? { id: saved.course_id, name: saved.course_name, holes: saved.course_holes } : null)
                        setEditHoleSet(saved?.which_nine || 'full')
                      }}
                    >
                      {editRound === roundIndex ? 'Close' : 'Edit'}
                    </button>
                  )}
                </div>

                {courseMode === 'varies' && editRound === roundIndex && (
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--green-mid)' }}>
                    {editCourse ? (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <span className="text-sm">{editCourse.name}</span>
                        <button className="btn btn-ghost" style={{ fontSize: '0.72rem' }}
                          onClick={() => setEditCourse(null)}>Change</button>
                      </div>
                    ) : (
                      <div style={{ marginBottom: 8 }}>
                        <CourseSearch onSelect={(c) => setEditCourse(normalizeCoursePick(c))} />
                      </div>
                    )}
                    <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Which holes</p>
                    <select className="input" value={editHoleSet}
                      onChange={(e) => setEditHoleSet(e.target.value)} style={{ marginBottom: 8 }}>
                      {HOLE_SET_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <button className="btn btn-primary btn-full" disabled={busy} onClick={saveRound}>
                      {busy ? 'Saving…' : 'Save round course'}
                    </button>
                  </div>
                )}

                <button
                  className="btn btn-ghost btn-full"
                  style={{ fontSize: '0.72rem', marginTop: 6 }}
                  disabled={busy || !saved?.course_id}
                  onClick={() => startRound(roundIndex)}
                >
                  Start round {roundIndex}
                </button>
              </div>
            )
          })}
        </div>
      </div>
      <Toast msg={toast} />
    </>
  )
}

// ─── Per-round schedule editor (B2T2) ────────────────────────────────────
//
// The commissioner CRUD surface for an event's / outing's rounds, superseding
// the B2T1 interim course cards (SingleEventCourseCard / OutingCourseCard —
// still defined above for their reused selectors + the B2T1 source-inspection
// tests, but no longer rendered). Each round is a full event_rounds row:
// play_date on ANY weekday (not just Fri/Sat/Sun), tee_time, course
// (CourseSearch), which_nine, is_scramble. Add / edit / remove ride B2T1's
// set_event_round + delete_event_round; reorder rides B2T2's
// reorder_event_rounds. Reads the event_rounds the extended
// get_event_admin_view returns; SUPPORTS THE EMPTY STATE (zero rounds → "Add
// first round"), which is how the owner re-enters the 5 lost schedules and the
// Masters-of-Mulligans draft. Permission is server-side (requireEventAdmin —
// owner or active co-organizer); this UI is only reachable by the owner surface.
function RoundScheduleEditor({ event, adminView, loading, refresh }) {
  const navigate = useNavigate()
  const { confirm } = useConfirm()
  const [toast, showToast] = useToast()
  const [busy, setBusy] = useState(false)
  // 'new' | round_number | null — which editor row is currently open.
  const [open, setOpen] = useState(null)
  const [form, setForm] = useState(blankRoundForm())

  const rounds = useMemo(() => sortEventRounds(adminView?.event_rounds), [adminView])

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }))
  const openNew = () => { setForm(blankRoundForm()); setOpen('new') }
  const openEdit = (row) => { setForm(eventRoundToForm(row)); setOpen(Number(row.round_number)) }
  const close = () => { setOpen(null); setForm(blankRoundForm()) }

  const save = async () => {
    if (busy) return
    if (!form.play_date) { showToast('Pick a date for this round.'); return }
    const round_number = open === 'new' ? nextEventRoundNumber(rounds) : open
    setBusy(true)
    try {
      await callAdmin('set_event_round', {
        event_id: event.id,
        round_number,
        course_id: form.course?.id || null,
        which_nine: form.which_nine,
        play_date: form.play_date,
        tee_time: form.tee_time || null,
        is_scramble: !!form.is_scramble,
      })
      showToast(open === 'new' ? `Round ${round_number} added.` : `Round ${round_number} saved.`)
      close()
      await refresh()
    } catch (e) { showToast(e.message) }
    setBusy(false)
  }

  const removeRound = async (roundNumber) => {
    if (busy) return
    const okToRemove = await confirm({
      title: `Remove round ${roundNumber}?`,
      message: 'This deletes the round from the schedule. Scores already recorded for it are not affected.',
      ok: 'Remove round', cancel: 'Keep it', danger: true,
    })
    if (!okToRemove) return
    setBusy(true)
    try {
      await callAdmin('delete_event_round', { event_id: event.id, round_number: roundNumber })
      if (open === roundNumber) close()
      showToast(`Round ${roundNumber} removed.`)
      await refresh()
    } catch (e) { showToast(e.message) }
    setBusy(false)
  }

  // Move a round up/down one slot: the pure helper computes the full reordered
  // id list, the action renumbers 1..N atomically (UNIQUE-safe park-then-assign).
  const move = async (roundNumber, direction) => {
    if (busy) return
    const ordered = reorderedEventRoundIds(rounds, roundNumber, direction)
    if (!ordered) return
    setBusy(true)
    try {
      await callAdmin('reorder_event_rounds', { event_id: event.id, ordered_ids: ordered })
      await refresh()
    } catch (e) { showToast(e.message) }
    setBusy(false)
  }

  const startRound = (row) => {
    if (!row?.course_id) { showToast('Pick a course for this round first.'); return }
    const prefill = buildPrefillFromEventRound(row)
    stashPrefill(prefill)
    navigate('/play', { state: { prefill } })
  }

  if (loading) return <p className="text-muted text-sm">Loading schedule…</p>

  return (
    <>
      <div className="card" style={{ marginTop: 12 }}>
        <h3 style={{ margin: 0, marginBottom: 8 }}>Rounds &amp; schedule</h3>
        <p className="text-xs text-muted" style={{ marginBottom: 10 }}>
          Add a round for each day of play — any weekday — with its course, tee
          time, and which nine. Reorder or remove rounds as plans change.
        </p>

        {rounds.length === 0 && open !== 'new' && (
          <p className="text-xs text-muted" style={{ marginBottom: 10 }}>
            No rounds scheduled yet.
          </p>
        )}

        <div style={{ display: 'grid', gap: 6 }}>
          {rounds.map((row, i) => {
            const rn = Number(row.round_number)
            const isOpen = open === rn
            return (
              <div key={row.id || rn} style={{ padding: '8px 10px', background: 'var(--green-deep)', borderRadius: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span className="text-sm">Round {rn}</span>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button className="btn btn-ghost" style={{ fontSize: '0.72rem' }}
                      disabled={busy || i === 0} onClick={() => move(rn, 'up')}
                      aria-label={`Move round ${rn} up`}>↑</button>
                    <button className="btn btn-ghost" style={{ fontSize: '0.72rem' }}
                      disabled={busy || i === rounds.length - 1} onClick={() => move(rn, 'down')}
                      aria-label={`Move round ${rn} down`}>↓</button>
                    <button className="btn btn-ghost" style={{ fontSize: '0.72rem' }}
                      disabled={busy} onClick={() => (isOpen ? close() : openEdit(row))}>
                      {isOpen ? 'Close' : 'Edit'}
                    </button>
                    <button className="btn btn-ghost" style={{ fontSize: '0.72rem', color: '#ff6b6b' }}
                      disabled={busy} onClick={() => removeRound(rn)}>Remove</button>
                  </div>
                </div>
                <div className="text-xs" style={{ color: 'var(--gray-300)', marginTop: 4 }}>
                  {roundSummary(row)}
                </div>
                {isOpen && (
                  <RoundFields form={form} setField={setField} busy={busy} onSave={save} saveLabel="Save round" />
                )}
                <button className="btn btn-ghost btn-full" style={{ fontSize: '0.72rem', marginTop: 6 }}
                  disabled={busy || !row.course_id} onClick={() => startRound(row)}>
                  Start round {rn}
                </button>
              </div>
            )
          })}
        </div>

        {open === 'new' ? (
          <div style={{ marginTop: 10, padding: '8px 10px', background: 'var(--green-deep)', borderRadius: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="text-sm">New round {nextEventRoundNumber(rounds)}</span>
              <button className="btn btn-ghost" style={{ fontSize: '0.72rem' }} disabled={busy} onClick={close}>Cancel</button>
            </div>
            <RoundFields form={form} setField={setField} busy={busy} onSave={save} saveLabel="Add round" />
          </div>
        ) : (
          <button className="btn btn-primary btn-full" style={{ marginTop: 10 }} disabled={busy} onClick={openNew}>
            {rounds.length === 0 ? 'Add first round' : 'Add round'}
          </button>
        )}
      </div>
      <Toast msg={toast} />
    </>
  )
}

function blankRoundForm() {
  return { round_number: null, course: null, which_nine: 'full', play_date: '', tee_time: '', is_scramble: false }
}

// One-line human summary of a round row for the collapsed list item.
function roundSummary(row) {
  const parts = []
  if (row.play_date) parts.push(toDateInputValue(row.play_date))
  if (row.tee_time) parts.push(toTimeInputValue(row.tee_time))
  parts.push(row.course_name
    ? `${row.course_name} · ${holeSetLabel(row.which_nine, row.course_holes)}`
    : 'No course assigned')
  if (row.is_scramble) parts.push('Scramble')
  return parts.join(' · ')
}

// The shared date / tee-time / course / which-nine / scramble field set, used by
// both the "new round" and the inline "edit round" editors.
function RoundFields({ form, setField, busy, onSave, saveLabel }) {
  return (
    <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--green-mid)' }}>
      <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Date (any weekday)</p>
      <input type="date" className="input" value={form.play_date}
        onChange={(e) => setField('play_date', e.target.value)} style={{ marginBottom: 8 }} />

      <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Tee time (optional)</p>
      <input type="time" className="input" value={form.tee_time}
        onChange={(e) => setField('tee_time', e.target.value)} style={{ marginBottom: 8 }} />

      <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Course</p>
      {form.course ? (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 8, padding: '6px 8px', background: 'var(--green-mid)', borderRadius: 6,
        }}>
          <span className="text-sm">{form.course.name || 'Selected course'}</span>
          <button className="btn btn-ghost" style={{ fontSize: '0.72rem' }}
            onClick={() => setField('course', null)}>Change</button>
        </div>
      ) : (
        <div style={{ marginBottom: 8 }}>
          <CourseSearch onSelect={(c) => setField('course', normalizeCoursePick(c))} />
        </div>
      )}

      <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Which holes</p>
      <select className="input" value={form.which_nine}
        onChange={(e) => setField('which_nine', e.target.value)} style={{ marginBottom: 8 }}>
        {HOLE_SET_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>

      <label className="text-sm" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <input type="checkbox" checked={!!form.is_scramble}
          onChange={(e) => setField('is_scramble', e.target.checked)} />
        Scramble round
      </label>

      <button className="btn btn-primary btn-full" disabled={busy} onClick={onSave}>
        {busy ? 'Saving…' : saveLabel}
      </button>
    </div>
  )
}

function LeagueTab({ event, adminView, loading, refresh }) {
  const cfg = adminView?.league_config
  const rounds = adminView?.league_rounds || []
  const [toast, showToast] = useToast()
  const [busy, setBusy] = useState(false)
  const [configError, setConfigError] = useState('')
  const [form, setForm] = useState({
    // Dates/time coerced into HTML-input shape so a saved row hydrates back
    // instead of blanking (F-081-T8): <input type="date"> needs 'YYYY-MM-DD',
    // <input type="time"> needs 'HH:MM'.
    season_start_date:    toDateInputValue(cfg?.season_start_date),
    season_end_date:      toDateInputValue(cfg?.season_end_date),
    cadence:              cfg?.cadence              || 'weekly',
    default_day_of_week:  cfg?.default_day_of_week ?? 4,   // Thursday
    default_start_time:   toTimeInputValue(cfg?.default_start_time) || '17:30',
    makeup_window_days:   cfg?.makeup_window_days   ?? 7,
    round_format_default: cfg?.round_format_default || 'stroke',
    absence_policy:       cfg?.absence_policy       || 'drop_lowest',
    drop_count:           cfg?.drop_count           ?? 2,
    handicap_factor:      cfg?.handicap_factor      || 'gross',
  })

  useEffect(() => {
    if (!cfg) return
    // Belt-and-suspenders: coerce date/time members before they reach the
    // inputs, in case a payload without the db.js normalization arrives.
    setForm(prev => ({ ...prev, ...normalizeLeagueConfigForForm(cfg) }))
  }, [cfg])

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }))

  // ── Course + which-nine (F-080) ──────────────────────────────────────
  // NOT persisted on league_configs (no column; a schema change was avoided).
  // 'same' feeds one course_id + hole_set into round generation (written to
  // every league_round); 'weekly' generates blank rounds the commissioner
  // assigns per week. The choice is inferred from existing rounds on load so a
  // returning admin sees the right mode.
  const [courseMode, setCourseMode] = useState('same')
  const [sameCourse, setSameCourse] = useState(null)   // { id, name, holes }
  const [sameHoleSet, setSameHoleSet] = useState('full')
  // Per-week inline course editor: the week_number currently open, plus its
  // staged course + hole_set before Save.
  const [editWeek, setEditWeek] = useState(null)
  const [editCourse, setEditCourse] = useState(null)
  const [editHoleSet, setEditHoleSet] = useState('full')
  const modeInferred = useMemo(() => {
    if (!rounds.length) return null
    const withCourse = rounds.filter(r => r.course_id)
    if (!withCourse.length) return 'weekly'
    const first = withCourse[0].course_id
    const allSame = rounds.every(r => r.course_id === first)
    return allSame ? 'same' : 'weekly'
  }, [rounds])
  // Sync the toggle to the inferred mode ONCE per event's rounds arriving, so a
  // returning admin lands on the right tab without clobbering a live toggle.
  const [modeSynced, setModeSynced] = useState(false)
  useEffect(() => {
    if (modeSynced || modeInferred == null) return
    setCourseMode(modeInferred)
    if (modeInferred === 'same') {
      const withCourse = rounds.find(r => r.course_id)
      if (withCourse) {
        setSameCourse({ id: withCourse.course_id, name: withCourse.course_name, holes: withCourse.course_holes })
        setSameHoleSet(withCourse.hole_set || 'full')
      }
    }
    setModeSynced(true)
  }, [modeInferred, modeSynced, rounds])

  const saveWeekCourse = async () => {
    if (busy || editWeek == null) return
    setBusy(true)
    try {
      await callAdmin('set_league_round', {
        event_id: event.id,
        week_number: editWeek,
        course_id: editCourse?.id || null,
        hole_set: editHoleSet,
      })
      showToast(`Week ${editWeek} course saved.`)
      setEditWeek(null); setEditCourse(null); setEditHoleSet('full')
      await refresh()
    } catch (e) { showToast(e.message) }
    setBusy(false)
  }

  const saveConfig = async () => {
    if (busy) return
    if (!form.season_start_date || !form.season_end_date) {
      showToast('Pick season start and end dates.'); return
    }
    setBusy(true)
    setConfigError('')
    try {
      await callAdmin('set_league_config', { event_id: event.id, ...form })
      showToast('League config saved.')
      await refresh()
    } catch (e) {
      // Surface the failure inline (not just the easily-missed toast) so an
      // owner editing a live league sees WHY the save was refused — e.g. the
      // season_underway lock on structural fields.
      setConfigError(e.message)
      showToast(e.message)
    }
    setBusy(false)
  }

  const generate = async () => {
    if (busy) return
    // Same-course mode requires a course before generating — that's the whole
    // point of the choice (every round is stamped with it). Weekly mode
    // generates blank rounds and the course is assigned per week afterward.
    if (courseMode === 'same' && !sameCourse?.id) {
      showToast('Pick the course for the season first.'); return
    }
    setBusy(true)
    try {
      const payload = { event_id: event.id }
      if (courseMode === 'same') {
        payload.course_id = sameCourse.id
        payload.hole_set = sameHoleSet
      }
      const res = await callAdmin('generate_league_rounds', payload)
      showToast(`Generated ${res.generated} weekly rounds.`)
      await refresh()
    } catch (e) { showToast(e.message) }
    setBusy(false)
  }

  if (loading) return <p className="text-muted text-sm">Loading league config…</p>

  const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

  return (
    <>
      <div className="card" style={{ marginBottom: 12 }}>
        <h3 style={{ margin: 0, marginBottom: 8 }}>Season</h3>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 8, marginBottom: 10 }}>
          <div>
            <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Start date</p>
            <input className="input" type="date" value={form.season_start_date}
              onChange={(e) => set('season_start_date', e.target.value)} />
          </div>
          <div>
            <p className="text-xs text-muted" style={{ marginBottom: 4 }}>End date</p>
            <input className="input" type="date" value={form.season_end_date}
              onChange={(e) => set('season_end_date', e.target.value)} />
          </div>
        </div>

        <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Cadence</p>
        <select className="input" value={form.cadence}
          onChange={(e) => set('cadence', e.target.value)}
          style={{ marginBottom: 10 }}>
          <option value="weekly">Weekly</option>
          <option value="biweekly">Every 2 weeks</option>
          <option value="monthly">Monthly</option>
          <option value="custom">Custom (I'll set dates myself)</option>
        </select>

        {form.cadence !== 'custom' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.1fr) minmax(0,1fr)', gap: 8, marginBottom: 10 }}>
            <div>
              <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Day of week</p>
              <select className="input" value={form.default_day_of_week}
                onChange={(e) => set('default_day_of_week', Number(e.target.value))}>
                {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
              </select>
            </div>
            <div>
              <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Tee time</p>
              <input className="input" type="time" value={form.default_start_time || ''}
                onChange={(e) => set('default_start_time', e.target.value)} />
            </div>
          </div>
        )}

        <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Default format</p>
        <select className="input" value={form.round_format_default}
          onChange={(e) => set('round_format_default', e.target.value)}
          style={{ marginBottom: 10 }}>
          <option value="stroke">Stroke play</option>
          <option value="match">Match play</option>
          <option value="scramble">Scramble</option>
          <option value="team_best_ball">Team best ball</option>
          <option value="stableford">Stableford</option>
        </select>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 8, marginBottom: 10 }}>
          <div>
            <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Make-up window (days)</p>
            <input className="input" type="number" min={0} max={30}
              value={form.makeup_window_days}
              onChange={(e) => set('makeup_window_days', Number(e.target.value))} />
          </div>
          <div>
            <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Handicap</p>
            <select className="input" value={form.handicap_factor}
              onChange={(e) => set('handicap_factor', e.target.value)}>
              <option value="gross">Gross</option>
              <option value="net_attested">Net — Attested Index</option>
              <option value="net_overall">Net — Overall Index</option>
            </select>
          </div>
        </div>

        <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Absence policy</p>
        <select className="input" value={form.absence_policy}
          onChange={(e) => set('absence_policy', e.target.value)}
          style={{ marginBottom: 10 }}>
          <option value="drop_lowest">Drop N lowest scores</option>
          <option value="hard_absence">Penalize absences</option>
          <option value="optional_attendance">Optional attendance</option>
        </select>

        {form.absence_policy === 'drop_lowest' && (
          <div style={{ marginBottom: 10 }}>
            <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Drop count</p>
            <input className="input" type="number" min={0} max={10}
              value={form.drop_count}
              onChange={(e) => set('drop_count', Number(e.target.value))} />
          </div>
        )}

        <button className="btn btn-primary btn-full" disabled={busy} onClick={saveConfig}>
          {busy ? 'Saving…' : cfg ? 'Update config' : 'Save config'}
        </button>
        {configError && (
          <p className="text-xs" style={{ color: 'var(--danger, #d9534f)', marginTop: 6, marginBottom: 0 }}>
            {configError}
          </p>
        )}
      </div>

      {/* Course & which-nine (F-080). The same/weekly toggle is locked once
          rounds are generated (regeneration is blocked server-side); after
          that, per-week edits happen in the Weekly rounds list below. */}
      {cfg && form.cadence !== 'custom' && (
        <div className="card" style={{ marginBottom: 12 }}>
          <h3 style={{ margin: 0, marginBottom: 8 }}>Course &amp; holes</h3>
          <p className="text-xs text-muted" style={{ marginBottom: 6 }}>
            How does the course change over the season?
          </p>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <button
              type="button"
              className={`btn ${courseMode === 'same' ? 'btn-primary' : 'btn-ghost'}`}
              style={{ flex: 1, fontSize: '0.8rem' }}
              disabled={rounds.length > 0}
              onClick={() => setCourseMode('same')}
            >
              Same course every week
            </button>
            <button
              type="button"
              className={`btn ${courseMode === 'weekly' ? 'btn-primary' : 'btn-ghost'}`}
              style={{ flex: 1, fontSize: '0.8rem' }}
              disabled={rounds.length > 0}
              onClick={() => setCourseMode('weekly')}
            >
              Course changes weekly
            </button>
          </div>

          {courseMode === 'same' ? (
            <>
              {sameCourse ? (
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '8px 10px', background: 'var(--green-deep)', borderRadius: 6, marginBottom: 10,
                }}>
                  <span className="text-sm">{sameCourse.name}</span>
                  {rounds.length === 0 && (
                    <button className="btn btn-ghost" style={{ fontSize: '0.75rem' }}
                      onClick={() => setSameCourse(null)}>Change</button>
                  )}
                </div>
              ) : rounds.length === 0 ? (
                <div style={{ marginBottom: 10 }}>
                  <CourseSearch onSelect={(c) => setSameCourse(normalizeCoursePick(c))} />
                </div>
              ) : null}
              <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Which holes</p>
              <select className="input" value={sameHoleSet} disabled={rounds.length > 0}
                onChange={(e) => setSameHoleSet(e.target.value)} style={{ marginBottom: 4 }}>
                {HOLE_SET_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </>
          ) : (
            <p className="text-xs text-muted">
              Each week starts blank — assign the course and which-nine per week
              in the list below (or leave it and set it when you pair that week).
            </p>
          )}
        </div>
      )}

      {cfg && form.cadence !== 'custom' && (
        <div className="card" style={{ marginBottom: 12 }}>
          <h3 style={{ margin: 0, marginBottom: 8 }}>Weekly rounds</h3>
          {rounds.length === 0 ? (
            <>
              <p className="text-muted text-sm" style={{ marginBottom: 10 }}>
                No rounds generated yet. Tap below to auto-create them from your
                season dates and cadence.
              </p>
              <button className="btn btn-primary btn-full" disabled={busy} onClick={generate}>
                Generate rounds
              </button>
            </>
          ) : (
            <div style={{ display: 'grid', gap: 6 }}>
              {rounds.map(r => (
                <div key={r.id} style={{
                  padding: '8px 10px', background: 'var(--green-deep)', borderRadius: 6,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <span className="text-sm">Week {r.week_number}</span>
                    <span className="text-muted text-xs">{r.scheduled_date}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                    <span className="text-xs" style={{ color: r.course_name ? 'var(--cream)' : 'var(--gray-400)' }}>
                      {r.course_name
                        ? `${r.course_name} · ${holeSetLabel(r.hole_set, r.course_holes)}`
                        : 'No course assigned'}
                    </span>
                    <button
                      className="btn btn-ghost"
                      style={{ fontSize: '0.72rem' }}
                      disabled={busy}
                      onClick={() => {
                        if (editWeek === r.week_number) { setEditWeek(null); return }
                        setEditWeek(r.week_number)
                        setEditCourse(r.course_id ? { id: r.course_id, name: r.course_name, holes: r.course_holes } : null)
                        setEditHoleSet(r.hole_set || 'full')
                      }}
                    >
                      {editWeek === r.week_number ? 'Close' : 'Edit'}
                    </button>
                  </div>

                  {editWeek === r.week_number && (
                    <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--green-mid)' }}>
                      {editCourse ? (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <span className="text-sm">{editCourse.name}</span>
                          <button className="btn btn-ghost" style={{ fontSize: '0.72rem' }}
                            onClick={() => setEditCourse(null)}>Change</button>
                        </div>
                      ) : (
                        <div style={{ marginBottom: 8 }}>
                          <CourseSearch onSelect={(c) => setEditCourse(normalizeCoursePick(c))} />
                        </div>
                      )}
                      <p className="text-xs text-muted" style={{ marginBottom: 4 }}>Which holes</p>
                      <select className="input" value={editHoleSet}
                        onChange={(e) => setEditHoleSet(e.target.value)} style={{ marginBottom: 8 }}>
                        {HOLE_SET_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      <button className="btn btn-primary btn-full" disabled={busy} onClick={saveWeekCourse}>
                        {busy ? 'Saving…' : 'Save week course'}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Toast msg={toast} />
    </>
  )
}
